import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { supabase } from './supabase';

// Determine environment variables
const supabaseFunctionsUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const isDevelopment = import.meta.env.DEV;

// Use environment variables if available, otherwise fallback to defaults
// For development, prefer local Supabase if no specific URL is provided
const graphqlEndpoint = supabaseFunctionsUrl 
  ? `${supabaseFunctionsUrl}/graphql` 
  : isDevelopment
    ? `${window.location.protocol}//${window.location.hostname}:54321/functions/v1/graphql` // Local Supabase URL
    : `${supabaseUrl}/functions/v1/graphql`; // Production URL

console.log(`Using GraphQL endpoint: ${graphqlEndpoint}`);

// Create an HTTP link to the GraphQL edge function
const httpLink = createHttpLink({
  uri: graphqlEndpoint,
  // Include credentials for cross-origin requests (important for CORS)
  credentials: 'include',
  // Apollo v4 requires this to prevent preflight requests that can cause CORS issues
  headers: {
    'apollo-require-preflight': 'true',
  },
});

// Check if a query is an introspection query to handle it differently
const isIntrospectionQuery = (operation: { operationName?: string; query?: { loc?: { source?: { body?: string } } } }) => {
  const operationName = operation.operationName;
  const query = operation.query?.loc?.source?.body;
  
  return (
    operationName === 'IntrospectionQuery' || 
    (query && (query.includes('__schema') || query.includes('__type')))
  );
};

// Error handling link to catch auth errors
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      console.error(`[GraphQL error]: ${err.message}`, err);
      // If we get an auth error, we'll try to refresh the session
      if (err.extensions?.code === 'UNAUTHENTICATED' || err.message?.includes('Unauthorized')) {
        console.warn('Authentication error, attempting to refresh session...');
        // We return null here and let the authLink handle refreshing
      }
    }
  }
  
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
    if (isDevelopment) {
      console.info('Check if local Supabase is running with `supabase status`');
      console.info(`Endpoint URL: ${graphqlEndpoint}`);
    }
  }
  
  return forward(operation);
});

// Set auth header with Supabase JWT
const authLink = setContext(async (operation, { headers }) => {
  try {
    // For introspection queries, add a special header
    if (isIntrospectionQuery(operation)) {
      return {
        headers: {
          ...headers,
          'x-apollo-operation-name': 'IntrospectionQuery',
          'apollo-require-preflight': 'true',
        }
      };
    }
    
    // First check if we have a valid session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      console.warn('No active session found');
      return { 
        headers: {
          ...headers,
          // Always include Apollo header even without auth
          'apollo-require-preflight': 'true',
        }
      };
    }

    // If session exists but is expired, try to refresh it
    if (session.expires_at && new Date(session.expires_at * 1000) < new Date()) {
      console.log('Session expired, refreshing...');
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('Failed to refresh auth token:', error);
        // Could redirect to login here if needed
        return { 
          headers: {
            ...headers,
            'apollo-require-preflight': 'true',
          }
        };
      }
      
      // Use the refreshed token if available
      if (data.session) {
        return {
          headers: {
            ...headers,
            authorization: `Bearer ${data.session.access_token}`,
            // Add Apollo specific headers to prevent preflight CORS issues
            'apollo-require-preflight': 'true',
          },
        };
      }
    }
    
    // Use existing token if available
    if (session?.access_token) {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${session.access_token}`,
          // Add Apollo specific headers to prevent preflight CORS issues
          'apollo-require-preflight': 'true',
        },
      };
    }

    return { 
      headers: {
        ...headers,
        // Always include Apollo header even without auth
        'apollo-require-preflight': 'true',
      }
    };
  } catch (error) {
    console.error('Error getting auth token:', error);
    return { 
      headers: {
        ...headers,
        // Always include Apollo header even with errors
        'apollo-require-preflight': 'true',
      }
    };
  }
});

// Create Apollo Client instance
const apolloClient = new ApolloClient({
  link: from([errorLink, authLink.concat(httpLink)]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // Add field policies for paginated queries
          listResponses: {
            keyArgs: ['reportId'],
            merge(existing = { edges: [], pageInfo: {} }, incoming) {
              return {
                ...incoming,
                edges: [...(existing.edges || []), ...incoming.edges],
              };
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      // Add retry on network errors
      nextFetchPolicy: 'cache-first',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

export default apolloClient; 